/*
 * One page's search performance: what Google showed it for and how often it was clicked, where it
 * ranks, what Google's index says about it, and (with GA4) the visits and sign-ups it brought.
 */
import { COUNT_Y_AXIS, linearSeries } from '@components/Common/Charts/chartAxes';
import { useChartColors } from '@components/Common/Charts/useChartColors';
import CRMStatusChip from '@components/Common/CRMStatusChip';
import EmptyState from '@components/Common/EmptyState';
import { SectionCard, WorkspaceField } from '@components/Common/EntityWorkspace';
import ErrorMessage from '@components/Common/ErrorMessage';
import FilterRow from '@components/Common/FilterRow';
import MetricTable, { type MetricColumn } from '@components/Common/MetricTable';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import StatTile from '@components/Common/StatTile';
import { formatDateTime } from '@helpers/date';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import ManageSearchOutlinedIcon from '@mui/icons-material/ManageSearchOutlined';
import MoreHorizOutlinedIcon from '@mui/icons-material/MoreHorizOutlined';
import QueryStatsOutlinedIcon from '@mui/icons-material/QueryStatsOutlined';
import ShowChartOutlinedIcon from '@mui/icons-material/ShowChartOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import SourceOutlinedIcon from '@mui/icons-material/SourceOutlined';
import TouchAppOutlinedIcon from '@mui/icons-material/TouchAppOutlined';
import TravelExploreOutlinedIcon from '@mui/icons-material/TravelExploreOutlined';
import { Box, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  PERFORMANCE_DAYS,
  type PerformanceDays,
  type SeoPage,
  type SeoPagePerformance,
  type SeoQueryStat,
  dayLabel,
  fetchPerformance,
  fmtCount,
  fmtCtr,
  fmtPosition,
  indexChip,
  primaryKeywordCallout,
  rangeLabel,
} from './seoData';

const DAYS_LABEL: Record<PerformanceDays, string> = {
  28: '28 days',
  90: '90 days',
  480: '16 months',
};

const QUERY_COLUMNS: MetricColumn<SeoQueryStat>[] = [
  {
    key: 'query',
    header: 'Query',
    variant: 'name',
    render: (q) => q.query,
    sortValue: (q) => q.query,
  },
  {
    key: 'clicks',
    header: 'Clicks',
    variant: 'strong',
    render: (q) => fmtCount(q.clicks),
    sortValue: (q) => q.clicks,
  },
  {
    key: 'impressions',
    header: 'Impressions',
    render: (q) => fmtCount(q.impressions),
    sortValue: (q) => q.impressions,
  },
  {
    key: 'ctr',
    header: 'CTR',
    render: (q) => fmtCtr(q.ctr),
    sortValue: (q) => q.ctr,
  },
  {
    key: 'position',
    header: 'Avg position',
    tooltip: '1 is the top result',
    render: (q) => fmtPosition(q.position),
    // Unranked last whichever way it is sorted is not possible here; treat it as far down.
    sortValue: (q) => q.position ?? 999,
  },
];

/** About eight ticks across the range, whatever its length, so 480 days does not print 480 labels. */
const tickEvery = (n: number) => Math.max(1, Math.ceil(n / 8));

const humanState = (s: string | null | undefined) => {
  if (!s) return '-';
  const t = s.replace(/_/g, ' ').toLowerCase();
  return t.charAt(0).toUpperCase() + t.slice(1);
};

const SeoPerformance: React.FC<{ page: SeoPage }> = ({ page }) => {
  const [days, setDays] = useState<PerformanceDays>(90);
  const [data, setData] = useState<SeoPagePerformance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [acc, teal, indigo, green] = useChartColors(4);

  const latest = useRef(0);
  const load = useCallback(async () => {
    const call = ++latest.current;
    setLoading(true);
    setError(null);
    try {
      const r = await fetchPerformance(page.path, days);
      if (call === latest.current) setData(r);
    } catch {
      if (call === latest.current)
        setError('The search figures could not be loaded.');
    } finally {
      if (call === latest.current) setLoading(false);
    }
  }, [page.path, days]);

  useEffect(() => {
    load();
  }, [load]);

  const callout = primaryKeywordCallout(
    page.primaryKeyword,
    page.primaryKeywordPosition,
  );
  const ga4 = page.sessions28 !== null && page.sessions28 !== undefined;
  const index = data?.googleIndex ?? page.googleIndex;
  const chip = indexChip(index);

  const controls = (
    <FilterRow>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={days}
        aria-label="Period"
        onChange={(_e, v) => {
          if (v !== null) setDays(v as PerformanceDays);
        }}
      >
        {PERFORMANCE_DAYS.map((d) => (
          <ToggleButton key={d} value={d}>
            {DAYS_LABEL[d]}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      {data && (
        <span className="seo-perf__range">{rangeLabel(data.from, data.to)}</span>
      )}
    </FilterRow>
  );

  const indexCard = (
    <SectionCard
      title="Google index"
      icon={<ManageSearchOutlinedIcon />}
      meta={index?.inspectedAt ? `Checked ${formatDateTime(index.inspectedAt)}` : undefined}
    >
      <WorkspaceField
        label="Verdict"
        value={<CRMStatusChip status={chip.status} label={chip.label} />}
      />
      <WorkspaceField label="Coverage" value={index?.coverageState ?? '-'} />
      <WorkspaceField
        label="Indexing allowed"
        value={humanState(index?.indexingState)}
      />
      <WorkspaceField
        label="Last crawl"
        mono
        value={index?.lastCrawlTime ? formatDateTime(index.lastCrawlTime) : '-'}
      />
      <WorkspaceField
        label="Google's canonical"
        full
        mono
        value={index?.googleCanonical ?? '-'}
      />
    </SectionCard>
  );

  if (loading && !data) return <SkeletonLoader rows={6} columns={4} />;
  if (error) return <ErrorMessage message={error} onRetry={load} />;

  const daily = data?.daily ?? [];
  const hasSearch = daily.some((d) => d.impressions > 0 || d.clicks > 0);

  if (!data || (!hasSearch && !page.searchSyncedAt)) {
    return (
      <>
        {callout && <p className="seo-perf__callout">{callout}</p>}
        <EmptyState
          icon={<QueryStatsOutlinedIcon />}
          title="Google data appears after the first sync"
          body='Use "Refresh Google data" on the SEO list to read Search Console and Google Analytics.'
        />
        {indexCard}
      </>
    );
  }

  const labels = daily.map((d) => dayLabel(d.date));
  const every = tickEvery(labels.length);
  const xAxis = [
    {
      data: labels,
      scaleType: 'point' as const,
      tickInterval: (_v: string, i: number) => i % every === 0,
      tickLabelStyle: { fontSize: 11 },
    },
  ];
  const margin = { top: 16, right: 16, bottom: 8, left: 8 };
  const c = data.channels;

  return (
    <>
      {callout && (
        <p
          className={`seo-perf__callout${
            page.primaryKeywordPosition === null ? ' seo-perf__callout--none' : ''
          }`}
        >
          {callout}
        </p>
      )}
      {controls}

      <SectionCard
        title="Clicks and impressions"
        icon={<TouchAppOutlinedIcon />}
        meta="Google Search"
      >
        <Box className="seo-perf__chart">
          {hasSearch ? (
            <LineChart
              xAxis={xAxis}
              yAxis={[
                { id: 'clicks', tickMinStep: 1, width: 40 },
                { id: 'impressions', tickMinStep: 1, position: 'right', width: 48 },
              ]}
              series={linearSeries([
                {
                  data: daily.map((d) => d.clicks),
                  label: 'Clicks',
                  color: acc,
                  yAxisId: 'clicks',
                  showMark: false,
                },
                {
                  data: daily.map((d) => d.impressions),
                  label: 'Impressions',
                  color: teal,
                  yAxisId: 'impressions',
                  showMark: false,
                },
              ])}
              height={260}
              margin={margin}
            />
          ) : (
            <EmptyState compact title="No clicks or impressions in this period" />
          )}
        </Box>
      </SectionCard>

      <SectionCard
        title="Average position"
        icon={<ShowChartOutlinedIcon />}
        meta="Higher on the chart is better"
      >
        <Box className="seo-perf__chart">
          {daily.some((d) => d.position !== null) ? (
            <LineChart
              xAxis={xAxis}
              yAxis={[{ reverse: true, min: 1, width: 40 }]}
              series={linearSeries([
                {
                  data: daily.map((d) => d.position),
                  label: 'Average position',
                  color: indigo,
                  showMark: false,
                  valueFormatter: (v: number | null) => fmtPosition(v),
                },
              ])}
              height={220}
              margin={margin}
            />
          ) : (
            <EmptyState compact title="Not ranking in this period" />
          )}
        </Box>
      </SectionCard>

      {ga4 && (
        <SectionCard
          title="Visits and sign-ups"
          icon={<TravelExploreOutlinedIcon />}
          meta="Google Analytics"
        >
          <Box className="seo-perf__chart">
            <LineChart
              xAxis={xAxis}
              yAxis={COUNT_Y_AXIS}
              series={linearSeries([
                {
                  data: daily.map((d) => d.sessions),
                  label: 'Visits',
                  color: acc,
                  showMark: false,
                },
                {
                  data: daily.map((d) => d.signups),
                  label: 'Sign-ups',
                  color: green,
                  showMark: false,
                },
              ])}
              height={220}
              margin={margin}
            />
          </Box>
        </SectionCard>
      )}

      <SectionCard
        title="Top queries"
        icon={<SearchOutlinedIcon />}
        meta="Last 28 days"
      >
        <Box className="seo-perf__full">
          <MetricTable<SeoQueryStat>
            columns={QUERY_COLUMNS}
            rows={data.topQueries}
            getRowKey={(q) => q.query}
            defaultSort={{ key: 'clicks', dir: 'desc' }}
            emptyLabel="No queries in the last 28 days"
          />
        </Box>
      </SectionCard>

      <SectionCard
        title="Where visits came from"
        icon={<SourceOutlinedIcon />}
        meta="Last 28 days"
      >
        <Box className="seo-perf__full">
          {ga4 ? (
            <Box className="cc-stat-grid">
              <StatTile label="Search" value={fmtCount(c.organic)} icon={<SearchOutlinedIcon />} />
              <StatTile
                label="AI assistants"
                value={fmtCount(c.ai)}
                icon={<AutoAwesomeOutlinedIcon />}
                sublabel="ChatGPT, Perplexity, Gemini and others"
              />
              <StatTile label="Direct" value={fmtCount(c.direct)} icon={<LinkOutlinedIcon />} />
              <StatTile label="Social" value={fmtCount(c.social)} icon={<ShareOutlinedIcon />} />
              <StatTile label="Referral" value={fmtCount(c.referral)} icon={<ForumOutlinedIcon />} />
              <StatTile label="Other" value={fmtCount(c.other)} icon={<MoreHorizOutlinedIcon />} />
            </Box>
          ) : (
            <EmptyState
              compact
              title="Connect Google Analytics"
              body="Visits by channel, including AI assistants, appear once Google Analytics is connected."
            />
          )}
        </Box>
      </SectionCard>

      {indexCard}
    </>
  );
};

export default SeoPerformance;
